import { catchError, firstValueFrom, Observable, take, throwError } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { ResponseActiveServices, UserActiveSubscriptionsResponse } from './api.model';

/**
 * Quanto o AppGuard reaproveita a consulta de assinaturas. Decidido em
 * 2026-09-22: uma assinatura cancelada no meio da sessao segue abrindo as telas
 * do sub-app ate este prazo vencer.
 */
export const ASSINATURAS_TTL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly http = inject(HttpClient);

  private apiUrl = environment.apiUrl;

  private _assinaturas?: { desde: number; resposta: Promise<UserActiveSubscriptionsResponse> };

  getServicesActive(): Promise<ResponseActiveServices> {
    return firstValueFrom(
      this.http.get<ResponseActiveServices>(`${this.apiUrl}/v1/services/active`).pipe(
        take(1),
        catchError(this.handleError)
      )
    );
  }

  getUsersActiveSubscriptions(): Promise<UserActiveSubscriptionsResponse> {
    return firstValueFrom(
      this.http.get<UserActiveSubscriptionsResponse>(`${this.apiUrl}/v1/user/active_subscriptions`).pipe(
        take(1),
        catchError(this.handleError)
      )
    );
  }

  /**
   * A mesma consulta, reaproveitada por ASSINATURAS_TTL_MS. O AppGuard roda em
   * toda navegacao do sub-app, e duas vezes ao entrar (rota pai, depois a
   * filha), a ~190 ms por chamada em homolog (2026-09-22) antes de o router
   * sequer baixar a tela. Chamadas simultaneas dividem a mesma requisicao. Erro
   * e resposta sem lista nao ficam guardados: a proxima navegacao pergunta de
   * novo em vez de repetir a falha por 5 minutos. A tela de servicos continua
   * perguntando fresco, pelo getUsersActiveSubscriptions().
   */
  getUsersActiveSubscriptionsCached(): Promise<UserActiveSubscriptionsResponse> {
    const agora = Date.now();
    if (this._assinaturas && agora - this._assinaturas.desde < ASSINATURAS_TTL_MS) {
      return this._assinaturas.resposta;
    }

    const resposta = this.getUsersActiveSubscriptions();
    const entrada = { desde: agora, resposta };
    this._assinaturas = entrada;
    const descartar = () => {
      if (this._assinaturas === entrada) {
        this._assinaturas = undefined;
      }
    };
    resposta.then(r => {
      if (!Array.isArray(r?.message)) {
        descartar();
      }
    }, descartar);
    return resposta;
  }

  private handleError(error: unknown): Observable<never> {
    console.error('API Error:', error);
    return throwError(() => error);
  }
}
